'use client';

import { CSSProperties, useEffect, useRef, useState } from 'react';
import { cn, getColorway } from 'utils/styles';
import { MainNavProps, NavContentLinkProps } from './types';
import { BackgroundColor, NavVariants, Colorways } from 'types/constants';
import TopNavBar from './TopNavBar';
import HorizontalNavBar from './HorizontalNavBar';
import NavContentMenu from './NavContentMenu';
import { AnimatePresence } from 'motion/react';
import useNavigationMenuTracking from 'components/analytics/hooks/useNavigationMenuTracking';
import { trackNavigationBarInteraction } from 'components/analytics/eventHandlerTracking';
import { usePage } from '@/contexts/PageProvider';

const getNavColor = ({
  isTransparent,
  scrollThreshold,
  menuIsOpen,
  pageColor,
}: {
  isTransparent?: boolean;
  scrollThreshold?: boolean;
  menuIsOpen?: boolean;
  pageColor?: Colorways | { name: Colorways };
}) => {
  // When scroll threshold is reached or menu is open, always use white
  if (scrollThreshold || menuIsOpen) {
    return BackgroundColor.White;
  }
  // When transparent nav is enabled, use transparent styling
  if (isTransparent) {
    return 'bg-transparent backdrop-blur-[6px] [mask-image:linear-gradient(white_75%,_transparent_100%)]';
  }
  // When transparent nav is disabled, use page color
  return getColorway(pageColor ?? Colorways.Primary);
};

const Nav = ({
  variant,
  cta,
  resourcesLabel,
  resourcesCollection,
  resourcesUrl,
  resourcesInternalPage,
  navContent,
  topBarLink,
  className,
}: MainNavProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [subMenuIsOpen, setSubMenuIsOpen] = useState<boolean>(false);
  const [activeSubMenu, setActiveSubMenu] = useState<NavContentLinkProps | null>(null);
  const [menuIsScrolled, setMenuIsScrolled] = useState<boolean>(false);
  const [contentMenuOffsetHeight, setContentMenuOffsetHeight] = useState(0);
  const ref = useRef<null | HTMLElement>(null);
  const [scrollThreshold, setScrollThreshold] = useState(false);
  const { visualSettings } = usePage();

  const showShadows = menuIsScrolled && menuIsOpen;

  const { track: navigationTrack } = useNavigationMenuTracking();

  const closeMenu = () => {
    setSubMenuIsOpen(false);
    setMenuIsOpen(false);
    setMenuIsScrolled(false);

    navigationTrack({
      action_detail: 'Sidebar Closed',
    });

    trackNavigationBarInteraction({
      button_label: 'Menu',
      action: 'close',
      level: '0',
    });
  };

  const openMenu = () => {
    setMenuIsOpen(true);

    navigationTrack({
      action_detail: 'Sidebar Opened',
    });

    trackNavigationBarInteraction({
      button_label: 'Menu',
      action: 'open',
      level: '0',
    });
  };

  const closeSubMenu = () => {
    setSubMenuIsOpen(false);
    setMenuIsOpen(true);

    navigationTrack({
      level: 1,
      action_detail: 'Sidebar Submenu Closed',
    });

    trackNavigationBarInteraction({
      button_label: activeSubMenu?.link?.label ?? 'Sidebar Submenu',
      action: 'close',
      level: '1',
    });
  };

  const openSubMenu = (subMenu: NavContentLinkProps) => {
    setActiveSubMenu(subMenu);
    setSubMenuIsOpen(true);

    navigationTrack({
      location: subMenu?.link?.label,
      level: 1,
      action_detail: 'Sidebar Submenu Opened',
    });

    trackNavigationBarInteraction({
      button_label: subMenu?.link?.label ?? 'Sidebar Submenu',
      action: 'open',
      level: '1',
    });
  };

  const toggleHorizontalSubmenu = (subMenu: NavContentLinkProps) => {
    if (subMenu === activeSubMenu) {
      setActiveSubMenu(null);
      navigationTrack({
        level: 1,
        action_detail: 'Horizontal Submenu Closed',
      });

      trackNavigationBarInteraction({
        button_label: subMenu?.link?.label ?? 'Horizontal Submenu',
        action: 'close',
        level: '1',
      });
    } else {
      setActiveSubMenu(subMenu);
      navigationTrack({
        location: subMenu?.link?.label,
        level: 1,
        action_detail: 'Horizontal Submenu Opened',
      });

      trackNavigationBarInteraction({
        button_label: subMenu?.link?.label ?? 'Horizontal Submenu',
        action: 'open',
        level: '1',
      });
    }
  };

  const handleMenuScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.target as HTMLDivElement;
    setMenuIsScrolled(!!scrollTop);
  };

  // Disable scrolling and interaction in the main document while the mobile menu is open
  useEffect(() => {
    const main = document.querySelector('main');
    const footer = document.querySelector('footer');
    if (menuIsOpen) {
      document.body.style.overflow = 'hidden';
      if (main != null) main.inert = true;
      if (footer != null) footer.inert = true;
    } else {
      document.body.style.overflow = 'unset';
      if (main != null) main.inert = false;
      if (footer != null) footer.inert = false;
    }
  }, [menuIsOpen]);

  useEffect(() => {
    if (!ref.current) return;
    const header = ref.current.closest('header');

    // Find the height of this nav's header parent element
    const currentElemHeight = header?.offsetHeight ?? 0;

    // Calculate the position from the top of page (accounts for presence of other header element)
    const offsetTop = (header?.offsetTop ?? 0) - window.pageYOffset;

    // Total height of any featured banner elements + this nav menu
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initializes offset height from DOM measurement
    setContentMenuOffsetHeight(offsetTop + currentElemHeight);
  }, [menuIsOpen]);

  useEffect(() => {
    if (!visualSettings?.transparentNav || !visualSettings?.moduleThresholdNavColorChange) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets scroll threshold when visual settings change
      setScrollThreshold(false);
      return;
    }

    const targetElement = document.querySelector(
      `[data-module-index='${visualSettings?.moduleThresholdNavColorChange}']`,
    );

    if (!targetElement || !ref.current) {
      setScrollThreshold(false);
      return;
    }

    const observerRef = { current: null as IntersectionObserver | null };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const navHeight = ref.current?.offsetHeight ?? 0;
        // Element is intersecting adjusted viewport OR has been scrolled past (above nav)
        const isPastThreshold = entry.isIntersecting || entry.boundingClientRect.top < navHeight;
        setScrollThreshold(isPastThreshold);
      });
    };

    const createObserver = () => {
      const navHeight = ref.current?.offsetHeight ?? 0;
      const bottomMargin = window.innerHeight - navHeight;

      observerRef.current?.disconnect();

      observerRef.current = new IntersectionObserver(handleIntersection, {
        threshold: 0,
        rootMargin: `0px 0px -${bottomMargin}px 0px`,
      });

      observerRef.current.observe(targetElement);
    };

    // Start transparent - observer will update based on actual intersection
    setScrollThreshold(false);

    createObserver();

    window.addEventListener('resize', createObserver);

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener('resize', createObserver);
    };
  }, [visualSettings?.moduleThresholdNavColorChange, visualSettings?.transparentNav]);

  if (variant === NavVariants.HORIZONTAL) {
    return (
      <nav
        className={cn('sticky top-0 left-0 right-0', BackgroundColor.White, className)}
        aria-label="Main navigation"
        data-testid="navHorizontal"
        style={{ '--menu-offset-height': `${contentMenuOffsetHeight}px` } as CSSProperties}
        ref={ref}>
        {/* Top Nav */}
        <HorizontalNavBar
          topBarLink={topBarLink}
          topBarCta={cta}
          showShadows={showShadows}
          navContent={navContent}
          openMenu={openMenu}
          closeMenu={closeMenu}
          menuIsOpen={menuIsOpen}
          closeSubMenu={closeSubMenu}
          subMenuIsOpen={subMenuIsOpen}
          activeSubMenu={activeSubMenu}
          handleSubMenuClick={toggleHorizontalSubmenu}
        />
        {/* Mobile Expanded Menu */}
        <AnimatePresence>
          {menuIsOpen && (
            <NavContentMenu
              subMenuIsOpen={subMenuIsOpen}
              showShadows={showShadows}
              cta={cta}
              activeSubMenu={activeSubMenu}
              resourcesCollection={resourcesCollection}
              resourcesLabel={resourcesLabel}
              resourcesInternalPage={resourcesInternalPage}
              resourcesUrl={resourcesUrl}
              navContent={navContent}
              handleMenuScroll={handleMenuScroll}
              handleSubMenuClick={openSubMenu}
            />
          )}
        </AnimatePresence>
      </nav>
    );
  }

  return (
    <nav
      className={cn(
        'sticky top-0 left-0 right-0 z-100',
        className,
        getNavColor({
          isTransparent: visualSettings?.transparentNav,
          scrollThreshold,
          menuIsOpen,
          pageColor: visualSettings?.pageColor,
        }),
        'transition-[background-color,opacity] duration-500',
      )}
      aria-label="Main navigation"
      data-testid="nav"
      style={{ '--menu-offset-height': `${contentMenuOffsetHeight}px` } as CSSProperties}
      ref={ref}>
      {/* Top Nav */}
      <TopNavBar
        menuIsOpen={menuIsOpen}
        subMenuIsOpen={subMenuIsOpen}
        topBarLink={topBarLink}
        topBarCta={cta}
        showShadows={showShadows}
        closeMenu={closeMenu}
        openMenu={openMenu}
        closeSubMenu={closeSubMenu}
      />

      {/* Main Expanded Menu */}
      <AnimatePresence>
        {menuIsOpen && (
          <NavContentMenu
            subMenuIsOpen={subMenuIsOpen}
            showShadows={showShadows}
            cta={cta}
            activeSubMenu={activeSubMenu}
            resourcesCollection={resourcesCollection}
            resourcesLabel={resourcesLabel}
            resourcesInternalPage={resourcesInternalPage}
            resourcesUrl={resourcesUrl}
            navContent={navContent}
            handleMenuScroll={handleMenuScroll}
            handleSubMenuClick={openSubMenu}
          />
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Nav;
